<template>
  <div class="post-list-container">
    <!-- Loading State -->
    <div v-if="!posts || posts.length === 0" class="loading-state">
      <div class="loading-card glass">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading amazing content...</p>
        <hr class="divider">
        <div class="promo-content">
          <h2 class="promo-title">
            <a href='https://apps.apple.com/us/app/wordcloudstudio-wordart-tool/id6504160406' 
               target='_blank'>
              ✨ WordCloudStudio - WordArt Tool ✨
            </a>
          </h2>
          <p class="promo-description">
            <a href='https://apps.apple.com/us/app/wordcloudstudio-wordart-tool/id6504160406' 
               target='_blank'>
              Create stunning word cloud / word art / text art effortlessly. 
              Ideal for marketers, educators, data enthusiasts, creatives, 
              business pros, event planners, and more!
            </a>
          </p>
        </div>
      </div>
    </div>
    
    <!-- Posts Grid -->
    <div v-else class="posts-grid">
      <article 
        v-for="(post, index) in posts" 
        :key="post.id" 
        class="post-card glass"
        :style="{ animationDelay: `${index * 0.1}s` }">
        
        <div class="card-content">
          <router-link :to="`/post/${post.id}`" class="post-title-link">
            <h2 class="post-title">
              {{ post.title.replace("<","").replace("/p>","").replace(">","").replace('"',"") }}
            </h2>
          </router-link>
          
          <div v-html="post.content.replace(post.title,'').substring(0, 800) + (post.content.length > 800 ? '...' : '')" 
               class="post-excerpt">
          </div>
        </div>
        
        <div class="card-footer">
          <router-link :to="`/post/${post.id}`" class="read-more-btn">
            <span>Read Full Article</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" 
                    stroke="currentColor" 
                    stroke-width="2" 
                    stroke-linecap="round" 
                    stroke-linejoin="round"/>
            </svg>
          </router-link>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PostList',
  props: {
    posts: Array,
  },
};
</script>

<style scoped>
.post-list-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-xl) var(--spacing-lg);
}

/* Loading State */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  animation: fadeIn 0.6s ease-out;
}

.loading-card {
  max-width: 600px;
  padding: var(--spacing-3xl);
  text-align: center;
  border-radius: var(--radius-2xl);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto var(--spacing-lg);
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: var(--font-size-lg);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
}

.divider {
  border: none;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.3), 
    transparent);
  margin: var(--spacing-xl) 0;
}

.promo-content {
  text-align: center;
}

.promo-title {
  font-size: var(--font-size-2xl);
  margin-bottom: var(--spacing-md);
}

.promo-title a {
  background: var(--primary-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
  transition: all var(--transition-base);
}

.promo-title a:hover {
  transform: scale(1.02);
}

.promo-description {
  font-size: var(--font-size-base);
  line-height: 1.8;
  color: var(--text-secondary);
}

.promo-description a {
  color: var(--accent-color);
  text-decoration: none;
  transition: color var(--transition-base);
}

.promo-description a:hover {
  color: var(--primary-color);
}

/* Posts Grid */
.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-2xl);
  animation: fadeIn 0.8s ease-out;
}

.post-card {
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-xl);
  padding: var(--spacing-2xl);
  transition: all var(--transition-base);
  opacity: 0;
  animation: fadeInUp 0.6s ease-out forwards;
  position: relative;
  overflow: hidden;
}

.post-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--primary-gradient);
  transform: scaleX(0);
  transition: transform var(--transition-base);
}

.post-card:hover::before {
  transform: scaleX(1);
}

.post-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 48px rgba(102, 126, 234, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.card-content {
  flex: 1;
  margin-bottom: var(--spacing-lg);
}

.post-title-link {
  text-decoration: none;
}

.post-title {
  font-size: var(--font-size-xl);
  font-weight: 700;
  background: var(--primary-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: var(--spacing-md);
  line-height: 1.3;
  transition: all var(--transition-base);
}

.post-card:hover .post-title {
  transform: translateX(4px);
}

.post-excerpt {
  font-size: var(--font-size-base);
  line-height: 1.7;
  color: var(--text-secondary);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
}

.post-excerpt :deep(p) {
  margin-bottom: var(--spacing-sm);
  color: var(--text-secondary);
}

.post-excerpt :deep(code) {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  font-size: 0.9em;
}

.post-excerpt :deep(pre) {
  background: rgba(255, 255, 255, 0.05);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  overflow-x: auto;
  margin: var(--spacing-sm) 0;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: var(--spacing-lg);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.read-more-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--primary-gradient);
  color: white;
  text-decoration: none;
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: var(--font-size-sm);
  transition: all var(--transition-base);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.read-more-btn svg {
  transition: transform var(--transition-base);
}

.read-more-btn:hover {
  transform: translateX(4px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.read-more-btn:hover svg {
  transform: translateX(4px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .post-list-container {
    padding: var(--spacing-lg) var(--spacing-md);
  }

  .posts-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }

  .post-card {
    padding: var(--spacing-xl);
  }

  .post-title {
    font-size: var(--font-size-lg);
  }

  .loading-card {
    padding: var(--spacing-2xl);
  }

  .promo-title {
    font-size: var(--font-size-xl);
  }
}

@media (max-width: 480px) {
  .post-list-container {
    padding: var(--spacing-md) var(--spacing-sm);
  }

  .post-card {
    padding: var(--spacing-lg);
  }

  .post-title {
    font-size: var(--font-size-base);
  }

  .post-excerpt {
    font-size: var(--font-size-sm);
    -webkit-line-clamp: 4;
  }
}
</style>